import { coins } from '../format';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { useGeeCoinAccount, type MarketProduct } from '../api';
import type { PendingCheckout } from '../checkout';
import { CoinMark, LoadError } from './MarketParts';

export function CheckoutDialog({
  product,
  pending,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  product: MarketProduct;
  pending: PendingCheckout | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (deliveryDetails: string | null) => void;
}) {
  const account = useGeeCoinAccount();
  const [delivery, setDelivery] = useState(pending?.request.deliveryDetails ?? '');
  const panel = useRef<HTMLDivElement>(null);
  const title = useId();
  const description = useId();
  const deliveryId = useId();
  const sufficient = account.data !== undefined && account.data.balance >= product.coinPrice;
  const disabled =
    busy ||
    (!pending &&
      (!sufficient ||
        account.isError ||
        product.stock === 0 ||
        (product.requiresDeliveryDetails && !delivery.trim())));

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const scroll = window.scrollY;
    const { position, top, width, overflow } = document.body.style;
    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${scroll}px`,
      width: '100%',
      overflow: 'hidden',
    });
    return () => {
      Object.assign(document.body.style, { position, top, width, overflow });
      window.scrollTo(0, scroll);
      previous?.focus();
    };
  }, []);
  useEffect(() => {
    function keyboard(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = panel.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), a[href]',
      );
      if (!items?.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === panel.current)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || document.activeElement === panel.current)
      ) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener('keydown', keyboard, true);
    return () => document.removeEventListener('keydown', keyboard, true);
  }, [onClose]);

  return createPortal(
    <div
      className="market-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title}
        aria-describedby={description}
        tabIndex={-1}
        className="market-modal"
      >
        <div className="market-modal-heading">
          <div>
            <p className="market-eyebrow">GEEFAMILY MARKET</p>
            <h2 id={title}>{pending ? 'Siparişini kontrol et' : 'Seçimini tamamla'}</h2>
          </div>
          <button className="market-close" aria-label="Kapat" onClick={onClose}>
            ×
          </button>
        </div>
        <p id={description} className="text-sm leading-relaxed text-surface-400">
          {pending
            ? 'Önceki işlemin aynı bilgileriyle devam edilecek. Sonuç doğrulanana kadar yeni sipariş başlatamazsın.'
            : '1 adet ürün için aşağıdaki GeeCoin tutarı hesabından düşülecek.'}
        </p>
        <div className="market-checkout-product">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt="" width={80} height={80} />
          ) : (
            <CoinMark />
          )}
          <div>
            <span>{product.category}</span>
            <h3>{product.title}</h3>
            <p>{coins(product.coinPrice)} GC · 1 adet</p>
          </div>
        </div>
        <p className="market-product-description">{product.description}</p>
        <dl className="market-checkout-totals">
          <div>
            <dt>Kullanılacak GeeCoin</dt>
            <dd>{coins(product.coinPrice)} GC</dd>
          </div>
          <div>
            <dt>Mevcut bakiyen</dt>
            <dd>
              {account.isPending
                ? 'Yükleniyor…'
                : account.isError
                  ? 'Yüklenemedi'
                  : `${coins(account.data.balance)} GC`}
            </dd>
          </div>
          {account.data && !pending && sufficient ? (
            <div>
              <dt>İşlem sonrası</dt>
              <dd>{coins(account.data.balance - product.coinPrice)} GC</dd>
            </div>
          ) : null}
        </dl>
        {account.isError ? (
          <LoadError
            label="Bakiyen doğrulanamadı."
            retry={() => {
              void account.refetch();
            }}
          />
        ) : null}
        {product.deliveryInstructions ? (
          <p className="market-delivery-instructions">{product.deliveryInstructions}</p>
        ) : null}
        {product.requiresDeliveryDetails ? (
          <div className="market-delivery">
            <label htmlFor={deliveryId}>
              Teslimat bilgileri <span>(zorunlu)</span>
            </label>
            <textarea
              id={deliveryId}
              value={delivery}
              onChange={(event) => setDelivery(event.target.value)}
              maxLength={2000}
              rows={3}
              disabled={busy || Boolean(pending)}
              placeholder="Ürün için istenen teslimat bilgilerini yaz."
            />
            <p>Bilgilerin yalnızca siparişini tamamlayan ekiple paylaşılır.</p>
          </div>
        ) : null}
        {!pending && account.data && !sufficient ? (
          <p className="market-insufficient" role="status">
            Bu ürün için {coins(product.coinPrice - account.data.balance)} GC daha gerekiyor.
          </p>
        ) : null}
        {error ? (
          <p className="market-checkout-error" role="alert">
            {error}
          </p>
        ) : null}
        <Button
          className="w-full mt-5"
          loading={busy}
          disabled={disabled}
          onClick={() => onSubmit(product.requiresDeliveryDetails ? delivery.trim() : null)}
        >
          {pending
            ? 'Aynı işlemi tekrar kontrol et'
            : `${coins(product.coinPrice)} GC kullan ve onayla`}
        </Button>
        <p className="market-checkout-fine">
          GeeCoin topluluk puanıdır; bu işlemde para ödemesi yapılmaz.
        </p>
      </div>
    </div>,
    document.body,
  );
}
